/**
 * useUtmCapture — first-touch UTM attribution
 *
 * Captures UTM params from URL on first landing → localStorage.
 * Persisted on signup → profiles.acquisition_* columns (AuthContext).
 * Phase A Bullseye decision support — measure CAC + activation per channel.
 */

import { useEffect } from 'react';

const STORAGE_KEY = 'utm_first_touch';

export interface UtmFirstTouch {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  referrer: string;
  landed_at: string;
}

export function useUtmCapture(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    if (!source) return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    const utm: UtmFirstTouch = {
      source,
      medium: params.get('utm_medium'),
      campaign: params.get('utm_campaign'),
      content: params.get('utm_content'),
      term: params.get('utm_term'),
      referrer: document.referrer || '',
      landed_at: new Date().toISOString(),
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
    } catch {
      // localStorage may be disabled (private browsing) — skip silently
    }
  }, []);
}

export function getUtmFirstTouch(): UtmFirstTouch | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UtmFirstTouch) : null;
  } catch {
    return null;
  }
}

export function clearUtmFirstTouch(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
